import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllParks } from '../../services';
import { getRecentMachines, pushRecentMachine } from '../../utils';
import * as s from './styled-command-palette';

// Busca global (Ctrl+K / Cmd+K): paleta de comando pra ir direto a um
// PARQUE, CLIENTE ou MAQUINA de qualquer tela. O indice deriva de
// /park/list (1 chamada, ja escopada por tenant — o usuario so encontra
// o que pode ver), cacheado em modulo por 5 min.

const INDEX_TTL_MS = 5 * 60 * 1000;
let indexCache = null;
let indexCacheAt = 0;

const MAX_PER_GROUP = 6;

// Normaliza pra busca: minusculas + sem acentos.
const norm = (str) =>
  (str || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const buildIndex = (parks) => {
  const parkEntries = [];
  const customerMap = new Map();
  const machineEntries = [];
  (parks || []).forEach((park) => {
    parkEntries.push({
      type: 'park',
      key: `p-${park.id}`,
      label: park.name,
      sub: (park.customer && park.customer.name) || park.state || '',
      park,
    });
    if (park.customer && park.customer.id != null) {
      customerMap.set(park.customer.id, {
        type: 'customer',
        key: `c-${park.customer.id}`,
        label: park.customer.name,
        sub: park.customer.estado || '',
        customer: park.customer,
        park,
      });
    }
    (park.appliance || []).forEach((appliance) => {
      (appliance.thing || []).forEach((thing) => {
        machineEntries.push({
          type: 'machine',
          key: `m-${thing.id}`,
          id: thing.id,
          label: thing.thing || thing.name,
          sub: [park.name, park.customer && park.customer.name]
            .filter(Boolean)
            .join(' · '),
          park,
          appliance,
        });
      });
    });
  });
  return {
    parks: parkEntries,
    customers: Array.from(customerMap.values()),
    machines: machineEntries,
  };
};

const GROUPS = [
  { id: 'machines', label: 'Máquinas' },
  { id: 'parks', label: 'Parques' },
  { id: 'customers', label: 'Clientes' },
];

// Acoes de navegacao (alem de ir pra um recurso). `roles` opcional gateia
// por papel — espelha o gating das rotas no App.js. keywords ajudam a
// achar por sinonimos.
const ACTIONS = [
  {
    key: 'a-dashboard',
    label: 'Ir para o Dashboard',
    to: '/dashboard',
    keywords: 'mapa inicio home parques',
  },
  {
    key: 'a-notif',
    label: 'Ver notificações / alarmes',
    to: '/notifications',
    keywords: 'alarme alerta pendente notificacao',
  },
  {
    key: 'a-reports',
    label: 'Ir para Relatórios',
    to: '/reports',
    keywords: 'relatorio export xls historico',
  },
  {
    key: 'a-customers',
    label: 'Gerenciar clientes',
    to: '/customers',
    keywords: 'cliente empresa cadastro',
    roles: ['higra'],
  },
  {
    key: 'a-stations',
    label: 'Gerenciar estações',
    to: '/stations',
    keywords: 'estacao parque cadastro',
    roles: ['higra'],
  },
  {
    key: 'a-machines',
    label: 'Gerenciar máquinas',
    to: '/machines',
    keywords: 'maquina thing cadastro',
    roles: ['higra'],
  },
  {
    key: 'a-users',
    label: 'Gerenciar usuários',
    to: '/users',
    keywords: 'usuario permissao acesso',
    roles: ['higra', 'admin'],
  },
  {
    key: 'a-variables',
    label: 'Gerenciar variáveis',
    to: '/variables',
    keywords: 'variavel range setpoint',
    roles: ['higra'],
  },
  {
    key: 'a-audit-logs',
    label: 'Ver logs de alteração',
    to: '/audit-logs',
    keywords: 'auditoria historico setpoint comando range alteracao',
    roles: ['higra'],
  },
];

const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const history = useHistory();
  const dispatch = useDispatch();
  const role = useSelector((state) => state.usuario?.user?.role);
  // Acoes visiveis pro papel atual (gating espelha as rotas do App.js).
  const allowedActions = useMemo(
    () => ACTIONS.filter((a) => !a.roles || a.roles.includes(role)),
    [role]
  );

  // Atalho global Ctrl+K / Cmd+K (e ESC pra fechar).
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Carrega o indice ao abrir (cache de modulo 5min).
  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    setCursor(0);
    // foco apos o mount do portal
    const t = setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 30);
    const now = Date.now();
    if (indexCache && now - indexCacheAt < INDEX_TTL_MS) {
      setIndex(indexCache);
    } else {
      setLoading(true);
      fetchAllParks()
        .then(({ data }) => {
          indexCache = buildIndex(data);
          indexCacheAt = Date.now();
          setIndex(indexCache);
        })
        .catch(() => setIndex({ parks: [], customers: [], machines: [] }))
        .finally(() => setLoading(false));
    }
    return () => clearTimeout(t);
  }, [open]);

  // Resultados filtrados, agrupados, com cap por grupo.
  const results = useMemo(() => {
    if (!index) return [];
    const q = norm(query.trim());
    const out = [];

    // Sem busca: "Recentes" primeiro — maquinas abertas recentemente,
    // casadas contra o indice (so as que o usuario ainda pode ver). Da
    // acesso de 1 clique as poucas maquinas que o operador mais visita.
    if (!q) {
      const byId = new Map(
        (index.machines || []).map((m) => [String(m.id), m])
      );
      const recent = getRecentMachines()
        .map((r) => byId.get(String(r.id)))
        .filter(Boolean)
        .slice(0, MAX_PER_GROUP);
      if (recent.length) out.push({ group: 'Recentes', items: recent });
    }

    GROUPS.forEach((g) => {
      const all = index[g.id] || [];
      const hits = q
        ? all.filter(
            (e) => norm(e.label).includes(q) || norm(e.sub).includes(q)
          )
        : all;
      if (hits.length) {
        out.push({ group: g.label, items: hits.slice(0, MAX_PER_GROUP) });
      }
    });

    // Acoes (navegacao): filtradas pela busca (label + keywords). Vao por
    // ultimo pra nao competir com os recursos buscados.
    const actionHits = (
      q
        ? allowedActions.filter(
            (a) =>
              norm(a.label).includes(q) || norm(a.keywords || '').includes(q)
          )
        : allowedActions
    ).map((a) => ({ ...a, type: 'action' }));
    if (actionHits.length) out.push({ group: 'Ações', items: actionHits });

    return out;
  }, [index, query, allowedActions]);

  const flat = useMemo(() => results.flatMap((r) => r.items), [results]);

  // Navegacao ao selecionar — espelha os fluxos existentes do dashboard.
  const go = useCallback(
    (entry) => {
      setOpen(false);
      if (!entry) return;
      if (entry.type === 'action') {
        history.push(entry.to);
        return;
      }
      if (entry.type === 'customer') {
        dispatch({
          type: 'SEGUNDO/SET_PARQUE_SELECIONADO',
          clienteSelecionado: entry.customer,
          parqueSelecionado: {},
        });
        history.push('/dashboard/customer', { ...entry.customer });
        return;
      }
      if (entry.type === 'park') {
        dispatch({
          type: 'SEGUNDO/SET_PARQUE_SELECIONADO',
          parqueSelecionado: entry.park,
          clienteSelecionado: entry.park.customer,
        });
        const appliances = entry.park.appliance || [];
        if (appliances.length === 1) {
          history.push('/dashboard/customer/plant', {
            ...appliances[0],
            ...entry.park,
          });
        } else {
          history.push('/dashboard/customer', {
            ...(entry.park.customer || {}),
          });
        }
        return;
      }
      // machine -> usina (plant) da aplicacao dela
      pushRecentMachine({ id: entry.id, label: entry.label, sub: entry.sub });
      dispatch({
        type: 'SEGUNDO/SET_PARQUE_SELECIONADO',
        parqueSelecionado: entry.park,
        clienteSelecionado: entry.park.customer,
      });
      history.push('/dashboard/customer/plant', {
        ...entry.appliance,
        ...entry.park,
      });
    },
    [dispatch, history]
  );

  const onInputKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(flat[cursor]);
    }
  };

  if (!open) return null;

  let flatIdx = -1;

  return ReactDOM.createPortal(
    <s.Backdrop
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <s.Panel role="dialog" aria-modal="true" aria-label="Busca global">
        <s.InputRow>
          {/* lucide:search */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            placeholder="Buscar máquina, parque ou cliente…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setCursor(0);
            }}
            onKeyDown={onInputKey}
            aria-label="Buscar máquina, parque ou cliente"
          />
          <s.EscHint>esc</s.EscHint>
        </s.InputRow>

        <s.Results>
          {loading ? (
            <s.Empty>Carregando índice…</s.Empty>
          ) : flat.length === 0 ? (
            <s.Empty>
              {query
                ? 'Nada encontrado — tente outro termo.'
                : 'Digite pra buscar no seu escopo.'}
            </s.Empty>
          ) : (
            results.map((r) => (
              <s.Group key={r.group}>
                <s.GroupLabel>{r.group}</s.GroupLabel>
                {r.items.map((item) => {
                  flatIdx += 1;
                  const idx = flatIdx;
                  return (
                    <s.Item
                      key={item.key}
                      type="button"
                      $active={idx === cursor}
                      onMouseEnter={() => setCursor(idx)}
                      onClick={() => go(item)}
                    >
                      <span className="lbl" title={item.label}>
                        {item.label}
                      </span>
                      {item.sub && (
                        <span className="sub" title={item.sub}>
                          {item.sub}
                        </span>
                      )}
                    </s.Item>
                  );
                })}
              </s.Group>
            ))
          )}
        </s.Results>

        <s.Foot>
          <span>↑↓ navegar</span>
          <span>↵ abrir</span>
          <span>esc fechar</span>
        </s.Foot>
      </s.Panel>
    </s.Backdrop>,
    document.body
  );
};

export default CommandPalette;
